import { useCallback, useRef, useState } from 'react';
import { debounce } from '../utils/debounce';

export function useAutoSave(
  saveFunction: (data: any) => Promise<any>,
  delay: number = 1000
) {
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const saveInProgressRef = useRef(false);

  const performSave = useCallback(async (data: any) => {
    if (saveInProgressRef.current) return;

    saveInProgressRef.current = true;
    setIsSaving(true);
    setError(null);

    try {
      await saveFunction(data);
      setLastSaved(new Date());
    } catch (err) {
      console.error('Auto-save failed:', err);
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setIsSaving(false);
      saveInProgressRef.current = false;
    }
  }, [saveFunction]);

  // Debounced save function
  const debouncedSave = useRef(
    debounce((data: any) => {
      performSave(data);
    }, delay)
  ).current;

  const save = useCallback((data: any) => {
    debouncedSave(data);
  }, [debouncedSave]);

  const forceSave = useCallback(async (data: any) => {
    debouncedSave.cancel();
    await performSave(data);
  }, [performSave, debouncedSave]);

  return {
    save,
    forceSave,
    isSaving,
    lastSaved,
    error
  };
}